import { createReducer, isAnyOf } from "@reduxjs/toolkit";
import { mergeWith } from "lodash";
import initialState from "./knowledge-base-item.store";
import { cancelSuccessful, clearDependentWorkspaces, create, createFailure, createSuccessful, getDependentWorkspacesSuccessful, getRestrictionsSuccessful, initDuplicating, linkTags, linkTagsFinish, makeUnsaved, reconnecting, recoverSuccessful, removeErrorByPropertyName, reset, saveSuccessful, setCurrentRequestTemplate, setDeleteTarget, setDependentCallback, setDependentDialogOpened, setShareOwnershipDialogOpened, setEditable, setEditableFailure, setEditableSuccessful, setPreventNavigation, setReadonly, setServerError, setSocketStarted, setupKnowledgeBaseItem, socketErrorMessage, unlinkTags, unlinkTagsFinish, update, updateFailure, updateSuccessful, updateTags, shareOwnership, shareOwnershipSuccessful, setRevokeOwnershipDialogOpened, revokeOwnershipSuccessful, revokeOwnership, } from "./knowledge-base-item.actions";
export default createReducer(initialState, (builder) => {
    builder
        .addCase(setupKnowledgeBaseItem, (state, { payload: { id, entityType, name, description, lastSavedDate, createdAt, createdBy, lastSavedBy, hasUnsavedChanges, tags, isLocked, isCompleted, owners, }, }) => ({
        ...state,
        id,
        entityType,
        name,
        description,
        lastSavedDate,
        createdAt,
        createdBy,
        lastSavedBy,
        tags,
        hasUnsavedChanges,
        isLocked,
        isCompleted,
        owners,
        inited: true,
    }))
        .addCase(setEditable, (state) => {
        state.editable = true;
    })
        .addCase(setEditableFailure, (state) => {
        state.editable = false;
    })
        .addCase(setEditableSuccessful, (state) => {
        state.isSubscribed = true;
    })
        .addCase(setSocketStarted, (state, { payload }) => {
        state.isSocketStarted = payload;
    })
        .addCase(reconnecting, (state) => {
        state.isSubscribed = false;
        state.isSocketStarted = false;
    })
        .addCase(setReadonly, (state) => {
        state.editable = false;
        state.isSubscribed = false;
    })
        .addCase(makeUnsaved, (state) => {
        state.saved = false;
        state.preventNavigation = true;
    })
        .addCase(recoverSuccessful, (state) => {
        state.saved = false;
        state.preventNavigation = true;
    })
        .addCase(create, (state) => {
        state.inProgress = true;
        state.serverError = null;
    })
        .addCase(createSuccessful, (state) => {
        state.inProgress = false;
    })
        .addCase(createFailure, (state) => ({
        ...initialState,
        currentRequestTemplate: state.currentRequestTemplate,
    }))
        .addCase(reset, (state) => ({ ...initialState, editable: state.editable }))
        .addCase(cancelSuccessful, (state) => {
        state.saved = true;
        state.hasUnsavedChanges = false;
        state.preventNavigation = false;
    })
        .addCase(saveSuccessful, (state, { payload }) => {
        state.saved = true;
        state.hasUnsavedChanges = false;
        state.preventNavigation = false;
        state.lastSavedDate = payload;
        state.createdAt = payload;
    })
        .addCase(unlinkTags, (state) => {
        state.tagsSaving = true;
    })
        .addCase(linkTags, (state) => {
        state.tagsSaving = true;
    })
        .addCase(linkTagsFinish, (state) => {
        state.tagsSaving = false;
    })
        .addCase(unlinkTagsFinish, (state) => {
        state.tagsSaving = false;
    })
        .addCase(updateTags, (state, { payload }) => {
        state.tags = payload;
        state.tagsSaving = false;
    })
        .addCase(setServerError, (state, { payload }) => {
        state.serverError = payload;
        state.currentRequestTemplate = payload === null ? null : state.currentRequestTemplate;
    })
        .addCase(removeErrorByPropertyName, (state, { payload }) => {
        const { serverError } = state;
        if (serverError) {
            const propertyNames = Array.isArray(payload)
                ? payload.map((propertyName) => propertyName.toLowerCase())
                : [payload.toLowerCase()];
            const errors = serverError.error.data.errors.filter(({ propertyName }) => !propertyNames.includes((propertyName ?? "").toLowerCase()));
            if (errors.length === 0) {
                state.serverError = null;
                state.currentRequestTemplate = payload === null ? null : state.currentRequestTemplate;
            }
            else if (serverError) {
                state.serverError = { ...serverError, error: { ...serverError.error, data: { errors } } };
            }
        }
    })
        .addCase(setCurrentRequestTemplate, (state, { payload }) => {
        state.currentRequestTemplate = payload;
    })
        .addCase(update, (state) => {
        state.inProgress = true;
    })
        .addCase(updateSuccessful, (state, { payload }) => {
        state.name = payload.name;
        state.description = payload.description;
        state.inProgress = false;
        state.serverError = null;
    })
        .addCase(updateFailure, (state) => {
        state.inProgress = false;
    })
        .addCase(getRestrictionsSuccessful, (state, { payload }) => {
        state.restrictions = mergeWith({ ...state.restrictions }, payload);
    })
        .addCase(initDuplicating, (state, { payload }) => {
        state.duplicating = payload;
    })
        .addCase(socketErrorMessage, (state, { payload }) => {
        state.socketError = payload;
    })
        .addCase(setDeleteTarget, (state, { payload }) => {
        state.deleteTarget = payload;
    })
        .addCase(setPreventNavigation, (state, { payload }) => {
        state.preventNavigation = payload;
    })
        .addCase(clearDependentWorkspaces, (state) => {
        state.dependentWorkspaces = undefined;
    })
        .addCase(getDependentWorkspacesSuccessful, (state, { payload }) => {
        state.dependentWorkspaces = payload;
    })
        .addCase(setDependentCallback, (state, { payload }) => {
        state.dependentCallback = payload;
    })
        .addCase(setDependentDialogOpened, (state, { payload }) => {
        state.isDependentDialogOpened = payload;
    })
        .addCase(setShareOwnershipDialogOpened, (state, { payload }) => {
        state.isShareOwnershipDialogOpened = payload;
    })
        .addCase(setRevokeOwnershipDialogOpened, (state, { payload }) => {
        state.isRevokeOwnershipDialogOpened = payload;
    })
        .addCase(shareOwnershipSuccessful, (state, { payload }) => {
        state.isShareOwnershipDialogOpened = false;
        state.ownershipEditInProgress = false;
        state.owners = payload.knowledgeBaseItem.owners;
    })
        .addCase(revokeOwnershipSuccessful, (state, { payload }) => {
        state.isRevokeOwnershipDialogOpened = false;
        state.ownershipEditInProgress = false;
        state.owners = payload.knowledgeBaseItem.owners;
    })
        .addMatcher(isAnyOf(shareOwnership, revokeOwnership), (state) => {
        state.ownershipEditInProgress = true;
    });
});
